import { useBookingConfirmationSummary, useDecryptAxeptaData } from '@massilia/queries'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { z } from 'zod'

import { PaymentCompleteComponent } from '@/components/payment-confirmation/payment-complete'
import { PaymentFailedComponent } from '@/components/payment-confirmation/payment-failed'
import { PaymentPendingComponent } from '@/components/payment-confirmation/payment-pending'

import { DepositPaymentConfirmation } from './-components/deposit-payment-confirmation'
import { InsurancePaymentConfirmation } from './-components/insurance-payment-confirmation'
import { buildPaymentConfirmationBookingDataFromSummary } from './-components/payment-confirmation-booking-data'
import { parsePaymentConfirmationUserData } from './-components/payment-confirmation-user-data'

// bookingRef comes from either:
// - Our own query param (non-CC direct redirect)
// - Axepta's UserData returned as a query param on the redirect URL
//   UserData formats:
//     BOOKING:   "<affidiumPnr>|<sessionId>"
//     DEPOSIT:   "deposit|<reference>|<amountCents>"
//     INSURANCE: "insurance|<reference>|<bookingId>|<sessionId?>"
// Data + Len are encrypted Axepta callback params appended to failure/success URLs
// session is the booking session ID passed via URL for non-CC direct redirects
const paymentConfirmationSearchSchema = z.object({
  mid: z.string().optional(),
  payId: z.string().optional(),
  transId: z.string().optional(),
  bookingRef: z.string().optional(),
  code: z.string().optional(),
  Code: z.coerce.string().optional(),
  Data: z.string().optional(),
  data: z.string().optional(),
  description: z.string().optional(),
  Description: z.string().optional(),
  Len: z.coerce.string().optional(),
  len: z.coerce.string().optional(),
  mac: z.string().optional(),
  MAC: z.string().optional(),
  MID: z.string().optional(),
  PayID: z.string().optional(),
  session: z.string().optional(),
  status: z.string().optional(),
  Status: z.string().optional(),
  TransID: z.string().optional(),
  UserData: z.string().optional(),
  userData: z.string().optional(),
})

export const Route = createFileRoute('/payment-confirmation/$status')({
  component: RouteComponent,
  validateSearch: paymentConfirmationSearchSchema,
})

function BookingConfirmation({
  bookingRef: bookingRefFromUserData,
  resolvedAxeptaDetails,
  searchParams,
  sessionIdFromUserData,
  status,
}: {
  bookingRef?: string
  resolvedAxeptaDetails: Record<string, string> | undefined
  searchParams: z.infer<typeof paymentConfirmationSearchSchema>
  sessionIdFromUserData?: string
  status: string
}) {
  // bookingRef: explicit param (non-CC) > parsed from UserData (CC) > undefined
  const bookingRef = searchParams.bookingRef ?? bookingRefFromUserData

  // sessionId: explicit param (non-CC direct redirect) > parsed from UserData (CC flow)
  const sessionId = searchParams.session ?? sessionIdFromUserData

  // Fetch booking summary from local DB via session ID (replaces Affidium mutation)
  const { data: summary } = useBookingConfirmationSummary(sessionId)

  const bookingData = useMemo(
    () => buildPaymentConfirmationBookingDataFromSummary(summary, bookingRef),
    [summary, bookingRef]
  )

  const statusComponents = {
    failed: (
      <PaymentFailedComponent
        axeptaDetails={resolvedAxeptaDetails}
        booking={bookingData}
        sessionId={sessionId}
        summary={summary}
      />
    ),
    pending: (
      <PaymentPendingComponent booking={bookingData} sessionId={sessionId} summary={summary} />
    ),
    success: (
      <PaymentCompleteComponent booking={bookingData} sessionId={sessionId} summary={summary} />
    ),
  } as const

  return statusComponents[normalizeStatus(status) as keyof typeof statusComponents] ?? statusComponents.pending
}

function buildPlainAxeptaDetails(searchParams: z.infer<typeof paymentConfirmationSearchSchema>) {
  const code = searchParams.Code ?? searchParams.code
  const description = searchParams.Description ?? searchParams.description
  const status = searchParams.Status ?? searchParams.status
  const transId = searchParams.TransID ?? searchParams.transId
  const payId = searchParams.PayID ?? searchParams.payId
  const userData = searchParams.UserData ?? searchParams.userData
  const mid = searchParams.MID ?? searchParams.mid
  const mac = searchParams.MAC ?? searchParams.mac

  if (!code && !description && !status && !transId && !payId && !userData) {
    return undefined
  }

  return {
    ...(code && { Code: code }),
    ...(description && { Description: description }),
    ...(status && { Status: status }),
    ...(transId && { TransID: transId }),
    ...(payId && { PayID: payId }),
    ...(userData && { UserData: userData }),
    ...(mid && { MID: mid }),
    ...(mac && { MAC: mac }),
  }
}

function normalizeStatus(status: string): 'failed' | 'success' | 'unknown' {
  if (status === 'success' || status === 'failed') return status
  return 'unknown'
}

function RouteComponent() {
  const { status } = Route.useParams()
  const searchParams = Route.useSearch()
  const axeptaData = searchParams.Data ?? searchParams.data
  const axeptaLen = searchParams.Len ?? searchParams.len

  // Decrypt Axepta error data when present (failure redirects include Data+Len)
  const { data: axeptaDetails } = useDecryptAxeptaData(axeptaData, axeptaLen)
  const plainAxeptaDetails = buildPlainAxeptaDetails(searchParams)
  const resolvedAxeptaDetails = axeptaDetails ?? plainAxeptaDetails

  const rawUserData =
    searchParams.UserData ?? searchParams.userData ?? resolvedAxeptaDetails?.UserData
  const parsedUserData = parsePaymentConfirmationUserData(rawUserData)

  // ── LEGACY DEPOSIT fork ──────────────────────────────────────────────────
  if (parsedUserData.flow === 'legacy_deposit') {
    return (
      <DepositPaymentConfirmation
        amountCents={parsedUserData.amountCents}
        reference={parsedUserData.bookingRef}
        status={normalizeStatus(status)}
      />
    )
  }

  // ── DEPOSIT fork ──────────────────────────────────────────────────────────
  if (parsedUserData.flow === 'deposit') {
    return (
      <DepositPaymentConfirmation
        amountCents={parsedUserData.amountCents}
        reference={parsedUserData.bookingRef}
        status={normalizeStatus(status)}
      />
    )
  }

  // ── INSURANCE fork ────────────────────────────────────────────────────────
  if (parsedUserData.flow === 'insurance') {
    return (
      <InsurancePaymentConfirmation
        reference={parsedUserData.bookingRef}
        status={normalizeStatus(status)}
      />
    )
  }

  // ── BOOKING fork (checkout CC + non-CC) ───────────────────────────────────
  const bookingRef = parsedUserData.flow === 'booking' ? parsedUserData.bookingRef : undefined
  const sessionIdFromUserData = parsedUserData.flow === 'booking' ? parsedUserData.sessionId : undefined

  return (
    <BookingConfirmation
      bookingRef={bookingRef}
      resolvedAxeptaDetails={resolvedAxeptaDetails}
      searchParams={searchParams}
      sessionIdFromUserData={sessionIdFromUserData}
      status={status}
    />
  )
}
